import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Auto-detect domain: use env variable, fallback to current window origin
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
      return null;
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
      setInitialized(true);
    }
  }, [token, fetchUser]);

  const login = async (identifier, password) => {
    const response = await axios.post(`${API}/auth/login`, { identifier, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(newUser);
    
    return newUser;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  // Derived state - computed from user object
  const isAuthenticated = !!user;
  const isAgency = user?.role === 'agency';
  const isProjectManager = user?.role === 'project_manager';
  const isTeamMember = user?.role === 'team_member' || user?.role === 'project_manager';
  const isClient = user?.role === 'client';
  const isCustomerService = user?.role === 'customer_service';
  const isAffiliate = user?.role === 'affiliate' || (user?.role === 'team_member' && user?.team_member_type === 'affiliate');
  
  // Check if user needs to complete onboarding
  // Onboarding is required if: not agency, not client, and onboarding not completed
  const needsOnboarding = user && 
    user.role !== 'agency' && 
    user.role !== 'client' &&
    !user.onboarding_completed;

  const value = {
    user,
    token,
    loading,
    initialized,
    login,
    register,
    logout,
    refreshUser: fetchUser,
    isAuthenticated,
    isAgency,
    isProjectManager,
    isTeamMember,
    isClient,
    isCustomerService,
    isAffiliate,
    needsOnboarding,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
